import { Component, DestroyRef, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { AanmerkelijkBelang, BokkzIbDataService, IncomeTaxEntity } from '@snelstart-ventures/fiscaal/domain-logic';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { debounceTime, filter, map, Observable, pairwise, shareReplay, startWith, Subscription, take } from 'rxjs';
import { optionsLandcodesMetNLD } from '../../gedeelde-componenten/gedeelde-componenten.component';
import { SharedUiDialogModule } from '@snelstart/ui-dialog';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { SharedUiTooltipModule } from '@snelstart/ui-tooltip';
import { CommonModule } from '@angular/common';
import { SharedUiAlertModule } from '@snelstart/ui-alert';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type AbFormGroup = FormGroup<{
    omschrijving: FormControl<string | null>;
    aantalAandelen: FormControl<number | null>;
    landCode: FormControl<string | null>;
    reguliereVoordelen: FormControl<number | null>;
    aftrekbareKosten: FormControl<number | null>;
    fictieveReguliereVoordelen: FormControl<number | null>;
    maximumBedragSchuldenBijVennootschappen: FormControl<number | null>;
    eerderInHeffingBetrokkenSchuldBijVennootschappen: FormControl<number | null>;
    schuldenVennootschappenBijImmigratie: FormControl<number | null>;
    totaalSchuldenBijVennootschappen: FormControl<number | null>;
    schuldenAangeverBijVennootschappen: FormControl<number | null>;
    schuldenPartnerBijVennootschappen: FormControl<number | null>;
    eigenWoningSchuldenBijVennootschappen: FormControl<number | null>;
    schuldenVerbondenPersonenBijVennootschappen: FormControl<number | null>;
    eigenWoningSchuldenVerbondenPersonenBijVennootschappen: FormControl<number | null>;
    toerekeningSchuldenVerbondenPersonenAanAbHouder: FormControl<number | null>;
    overdrachtsprijs: FormControl<number | null>;
    toerekenbareVerkrijgingsprijs: FormControl<number | null>;
    verzoekDoorschuivenVerkrijgingsPrijs: FormControl<boolean | null>;
    vennootschapBevatBeleggingsVermogen: FormControl<boolean | null>;
}>;

@Component({
    selector: 'snelstart-venture-fiscaal-feature-box2-inkomen-uit-aanmerkelijk-belang',
    templateUrl: './box2-inkomen-uit-aanmerkelijk-belang.component.html',
    styleUrls: ['./box2-inkomen-uit-aanmerkelijk-belang.component.scss'],
    imports: [
        SharedUiFormControlsModule,
        SharedUiDialogModule,
        SharedUiButtonModule,
        SharedUiDetailsModule,
        SharedUiTooltipModule,
        CommonModule,
        SharedUiAlertModule
    ]
})
export class Box2InkomenUitAanmerkelijkBelangComponent implements OnInit, OnDestroy {

    @Input() public id!: string;
    private dataService = inject(BokkzIbDataService);
    private destroyRef = inject(DestroyRef);
    private changeSubscriptions = new Subscription();
    public hasBuitenlandsLandcode = false;
    public dialogVerkoopVisible = false;
    public dialogLenenVisible = false;
    public isDefAangifte = false;
    public index = 0;

    public itemsVerkrijgingsprijs: SelectFormControlItem<boolean | undefined>[] = [
        { value: undefined, label: '--- selecteer ---' },
        { value: false, label: 'Niet doorschuiven' },
        { value: true, label: 'Wel doorschuiven' }
    ];

    public itemsBeleggingsvermogen: SelectFormControlItem<boolean | undefined>[] = [
        { value: undefined, label: '--- selecteer ---' },
        { value: false, label: 'Geen beleggingsvermogen' },
        { value: true, label: 'Wel beleggingsvermogen' }
    ];

    public optionsLandcodes: SelectFormControlItem<string>[] = optionsLandcodesMetNLD;

    public formGroup = new FormGroup({
        aanmerkelijkBelang: new FormArray<AbFormGroup>([])
    });

    readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
        this.dataService.incomeTaxEntity$.pipe(
            filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
            takeUntilDestroyed(this.destroyRef),
            shareReplay({ bufferSize: 1, refCount: true })
        );

    ngOnInit(): void {
        this.incomeTaxEntity$.pipe(
            take(1),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(entity => {
            this.isDefAangifte = entity.taxType === 'DA';
            this.formGroup.reset({}, { emitEvent: false }); // Prevent reset from emitting events
            this.updateForm(entity?.aangifte?.box2?.aanmerkelijkBelang || []);
            this.checkBuitenlandsLandcode();
        });
        this.subscribeToChanges();
    }

    ngOnDestroy(): void {
        this.changeSubscriptions.unsubscribe();
    }

    private subscribeToChanges(): void {
        this.changeSubscriptions.unsubscribe();
        this.changeSubscriptions = new Subscription();

        this.subscribeToBedragChanges(this.aanmerkelijkBelang);
    }

    private subscribeToBedragChanges(formArray: FormArray<AbFormGroup>): void {
        const extractBedragen = () => formArray.getRawValue().map(item => ({
            reguliereVoordelen: item.reguliereVoordelen,
            aftrekbareKosten: item.aftrekbareKosten,
            overdrachtsprijs: item.overdrachtsprijs,
            toerekenbareVerkrijgingsprijs: item.toerekenbareVerkrijgingsprijs
        }));

        this.changeSubscriptions.add(
            formArray.valueChanges.pipe(
                debounceTime(500),
                map(() => this.saveForm()),
                map(() => extractBedragen()),
                startWith(extractBedragen()),
                pairwise(),
                filter(([prev, curr]) => JSON.stringify(prev) !== JSON.stringify(curr)),
                takeUntilDestroyed(this.destroyRef)
            ).subscribe(() => {
                this.dataService.calculateLatestIncomeTaxReturn().subscribe();
            })
        );
    }

    showDialogVerkoop(index: number) {
        this.index = index;
        this.dialogVerkoopVisible = true;
    }

    showDialogLenen(index: number) {
        this.index = index;
        this.dialogLenenVisible = true;
    }

    get aanmerkelijkBelang(): FormArray<AbFormGroup> {
        return this.formGroup.controls.aanmerkelijkBelang;
    }

    addRij(): void {
        this.aanmerkelijkBelang.push(this.createFormGroup(), { emitEvent: false });
    }

    verwijderRij(index: number): void {
        this.aanmerkelijkBelang.removeAt(index, { emitEvent: false });
        this.formGroup.updateValueAndValidity({ emitEvent: false });
        this.formGroup.markAsDirty({ emitEvent: false });
        this.saveForm();
        this.subscribeToChanges();
    }

    createFormGroup(item?: AanmerkelijkBelang): FormGroup {
        return new FormGroup({
            omschrijving: new FormControl<string | null>(item?.omschrijving ?? null),
            aantalAandelen: new FormControl<number | null>(item?.aantalAandelen ?? null),
            landCode: new FormControl<string | null>(item?.landCode ?? null),
            reguliereVoordelen: new FormControl<number | null>(item?.reguliereVoordelen ?? null),
            aftrekbareKosten: new FormControl<number | null>(item?.aftrekbareKosten ?? null),
            overdrachtsprijs: new FormControl<number | null>(item?.overdrachtsprijs ?? null),
            toerekenbareVerkrijgingsprijs: new FormControl<number | null>(item?.toerekenbareVerkrijgingsprijs ?? null),
            verzoekDoorschuivenVerkrijgingsPrijs: new FormControl<boolean | null>(item?.verzoekDoorschuivenVerkrijgingsPrijs ?? null),
            vennootschapBevatBeleggingsVermogen: new FormControl<boolean | null>(item?.vennootschapBevatBeleggingsVermogen ?? null),
            totaalSchuldenBijVennootschappen: new FormControl<number | null>(item?.totaalSchuldenBijVennootschappen ?? null),
            eigenWoningSchuldenBijVennootschappen: new FormControl<number | null>(item?.eigenWoningSchuldenBijVennootschappen ?? null),
            eerderInHeffingBetrokkenSchuldBijVennootschappen: new FormControl<number | null>(item?.eerderInHeffingBetrokkenSchuldBijVennootschappen ?? null),
            fictieveReguliereVoordelen: new FormControl<number | null>(item?.fictieveReguliereVoordelen ?? null),
            maximumBedragSchuldenBijVennootschappen: new FormControl<number | null>(item?.maximumBedragSchuldenBijVennootschappen ?? null),
            schuldenVennootschappenBijImmigratie: new FormControl<number | null>(item?.schuldenVennootschappenBijImmigratie ?? null),
            schuldenAangeverBijVennootschappen: new FormControl<number | null>(item?.schuldenAangeverBijVennootschappen ?? null),
            schuldenPartnerBijVennootschappen: new FormControl<number | null>(item?.schuldenPartnerBijVennootschappen ?? null),
            schuldenVerbondenPersonenBijVennootschappen: new FormControl<number | null>(
                item?.schuldenVerbondenPersonenBijVennootschappen ?? null
            ),
            eigenWoningSchuldenVerbondenPersonenBijVennootschappen: new FormControl<number | null>(
                item?.eigenWoningSchuldenVerbondenPersonenBijVennootschappen ?? null
            ),
            toerekeningSchuldenVerbondenPersonenAanAbHouder: new FormControl<number | null>(
                item?.toerekeningSchuldenVerbondenPersonenAanAbHouder ?? null
            )
        });
    }

    checkBuitenlandsLandcode(): void {
        const aanmerkelijkBelangArray = this.aanmerkelijkBelang.controls;
        for (const item of aanmerkelijkBelangArray) {
            const landCode = item.get('landCode')?.value;
            if (landCode && landCode !== 'NLD') {
                this.hasBuitenlandsLandcode = true;
                return;
            }
        }
        this.hasBuitenlandsLandcode = false;
    }

    private updateForm(data: AanmerkelijkBelang[]): void {
        const lijst = data || [];
        const formArray = this.aanmerkelijkBelang;

        while (formArray.length > lijst.length) {
            formArray.removeAt(formArray.length - 1, { emitEvent: false });
        }

        for (let i = 0; i < lijst.length; i++) {
            const item = lijst[i];
            if (i < formArray.length) {
                const control = formArray.at(i) as FormGroup;
                control.patchValue(item, { emitEvent: false });
            } else {
                const formGroup = this.createFormGroup(item);
                formArray.push(formGroup, { emitEvent: false });
            }
        }
    }

    saveForm(): void {
        this.formGroup.updateValueAndValidity({ emitEvent: false });

        const items: AanmerkelijkBelang[] = this.formGroup.getRawValue().aanmerkelijkBelang
            .filter(item => item.landCode || item.omschrijving || item.aantalAandelen != null)
            .map(item => ({
                omschrijving: item.omschrijving ?? undefined,
                aantalAandelen: item.aantalAandelen ?? undefined,
                landCode: item.landCode ?? undefined,
                reguliereVoordelen: item.reguliereVoordelen ?? undefined,
                aftrekbareKosten: item.aftrekbareKosten ?? undefined,
                overdrachtsprijs: item.overdrachtsprijs ?? undefined,
                toerekenbareVerkrijgingsprijs: item.toerekenbareVerkrijgingsprijs ?? undefined,
                verzoekDoorschuivenVerkrijgingsPrijs: item.verzoekDoorschuivenVerkrijgingsPrijs ?? undefined,
                vennootschapBevatBeleggingsVermogen: item.vennootschapBevatBeleggingsVermogen ?? undefined,
                totaalSchuldenBijVennootschappen: item.totaalSchuldenBijVennootschappen ?? undefined,
                eigenWoningSchuldenBijVennootschappen: item.eigenWoningSchuldenBijVennootschappen ?? undefined,
                fictieveReguliereVoordelen: item.fictieveReguliereVoordelen ?? undefined,
                maximumBedragSchuldenBijVennootschappen: item.maximumBedragSchuldenBijVennootschappen ?? undefined,
                eerderInHeffingBetrokkenSchuldBijVennootschappen: item.eerderInHeffingBetrokkenSchuldBijVennootschappen ?? undefined,
                schuldenVennootschappenBijImmigratie: item.schuldenVennootschappenBijImmigratie ?? undefined,
                schuldenAangeverBijVennootschappen: item.schuldenAangeverBijVennootschappen ?? undefined,
                schuldenPartnerBijVennootschappen: item.schuldenPartnerBijVennootschappen ?? undefined,
                schuldenVerbondenPersonenBijVennootschappen: item.schuldenVerbondenPersonenBijVennootschappen ?? undefined,
                eigenWoningSchuldenVerbondenPersonenBijVennootschappen: item.eigenWoningSchuldenVerbondenPersonenBijVennootschappen ?? undefined,
                toerekeningSchuldenVerbondenPersonenAanAbHouder: item.toerekeningSchuldenVerbondenPersonenAanAbHouder ?? undefined
            }));

        const sumEerderInHeffing = items.reduce(
            (sum, item) => sum + (item.eerderInHeffingBetrokkenSchuldBijVennootschappen ?? 0), 0
        );


        this.dataService.patchIncomeTaxEntity(currentEntity => {
            if (!currentEntity.aangifte) {
                return currentEntity;
            }

            return {
                ...currentEntity,
                aangifte: {
                    ...currentEntity.aangifte,
                    box2: {
                        ...currentEntity.aangifte.box2,
                        aanmerkelijkBelang: items,
                        eerderInHeffingBetrokkenSchuldBijVennootschappen: sumEerderInHeffing
                    }
                }
            } as IncomeTaxEntity;
        });
    }

}
